import { useCallback, useEffect, useRef } from 'react';

import { logger, monitoring } from 'common/services';
import { formsApi, useGetFormQuery, useSubmitFormMutation } from 'modules/forms/api';
import { formClosed, selectActiveForm } from 'modules/forms/state';
import { useAppDispatch, useAppSelector, useAppStore } from 'store';

import { FormDialog } from '../FormDialog';

import type { FormAnswers } from 'modules/forms/types';

/** Renders whichever form a trigger has requested. Mounted once, in PreApp. */
export const FormDialogHost = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const active = useAppSelector(selectActiveForm);
  const { data: form, isError, isFetching, isSuccess } = useGetFormQuery(active?.formKey ?? '', { skip: !active });
  const [submitForm] = useSubmitFormMutation();
  const shownRef = useRef<string | null>(null);

  const loadFailed = isError && !isFetching;
  // The mapper returns null for a definition this build can't render whole (e.g. an unknown question type).
  const unusable = !!active && (loadFailed || (isSuccess && form === null));

  useEffect(() => {
    if (!unusable || !active) return;
    // A failed prefetch (EventRoom) leaves a rejected cache entry. The first render serves that stale error,
    // and the refetch it triggers is dispatched by the query hook's own effect, which runs before this one.
    // So the live entry is already loading here, and only the outcome of that request counts.
    if (formsApi.endpoints.getForm.select(active.formKey)(store.getState()).isLoading) return;
    const context = { formKey: active.formKey, reason: loadFailed ? 'load_failed' : 'unsupported_definition' };
    logger.error('[FormDialogHost] Form definition unavailable; not showing the form', context);
    monitoring.reportError(new Error('Form definition unavailable'), { context: 'forms.load_failed', ...context });
    dispatch(formClosed());
  }, [unusable, active, loadFailed, dispatch, store]);

  useEffect(() => {
    if (active && form && shownRef.current !== active.formKey) {
      shownRef.current = active.formKey;
      monitoring.trackAction('forms.shown', { formKey: active.formKey });
    }
    if (!active) shownRef.current = null;
  }, [active, form]);

  // Only the form key and HTTP status go to logger and monitoring, never answer text.
  const handleSubmit = useCallback(
    async (answers: FormAnswers): Promise<'ok' | 'error'> => {
      if (!active || !form) return 'error';
      const result = await submitForm({
        formKey: active.formKey,
        body: { formVersion: form.version, subject: active.subject, context: active.context, answers },
      });
      const failed = 'error' in result;
      const status = failed ? (result.error as { status?: number }).status : undefined;
      // 409: this person already submitted this form for this subject. That is the outcome they wanted.
      if (!failed || status === 409) {
        monitoring.trackAction('forms.submitted', { formKey: active.formKey });
        return 'ok';
      }
      monitoring.trackAction('forms.submit_failed', { formKey: active.formKey, status });
      logger.error('[FormDialogHost] Form submission failed', { formKey: active.formKey, status });
      monitoring.reportError(new Error('Form submission failed'), {
        context: 'forms.submit_failed',
        formKey: active.formKey,
        status,
      });
      return 'error';
    },
    [active, form, submitForm]
  );

  // `forms.submitted` is tracked when the save succeeds, so closing the thank-you screen tracks nothing.
  const handleClose = useCallback(
    (outcome: 'dismissed' | 'submitted', step: number) => {
      if (active && outcome === 'dismissed') {
        monitoring.trackAction('forms.dismissed', { formKey: active.formKey, step });
      }
      dispatch(formClosed());
    },
    [active, dispatch]
  );

  if (!active || !form) return null;
  return <FormDialog form={form} onSubmit={handleSubmit} onClose={handleClose} />;
};
